import React from "react"
import { Tab, Tabs } from "react-bootstrap"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useControlledState } from "shared/lib/useControlledState"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { FloorFormTabType } from "features/floor-management/types/FloorFormTabType"

import { useFloorFormAttachmentsContext } from "../context/FloorFormAttachmentsContext"
import { useFloorFormContext } from "../contexts/FloorFormContext"
import { FloorFormAttachmentsTab } from "./FloorFormAttachmentsTab"
import { FloorFormClarificationRequest } from "./FloorFormClarificationRequest"
import { FloorFormRemarkTab } from "./FloorFormRemarkTab"

export interface FloorFormTabsProps {
  activeTab?: FloorFormTabType
  defaultTab?: FloorFormTabType
  onTabChange?: (tab: FloorFormTabType) => void
}

export const FloorFormTabs = React.memo(
  ({ activeTab, defaultTab, onTabChange }: FloorFormTabsProps) => {
    const { t } = useTranslation()
    const {
      isReadonly,
      model,
      hideRemark,
      hideClarificationRequest,
      hideAttachments
    } = useFloorFormContext()
    const { linkedAttachments } = useFloorFormAttachmentsContext()

    const remark = useWatch({ name: "floor.remark" })
    const hasClarificationRequest =
      !!model?.core.data.floor?.data?.hasClarificationRequest
    const showRemarkTab = !hideRemark && (!isReadonly || !!remark)
    const showAttachmentsTab =
      !hideAttachments && ((linkedAttachments?.length ?? 0) > 0 || !isReadonly)
    const showClarificationRequestTab =
      !hideClarificationRequest && (!isReadonly || hasClarificationRequest)

    function getDefaultActiveTab() {
      if (showRemarkTab) return FloorFormTabType.REMARK
      if (showAttachmentsTab) return FloorFormTabType.ATTACHMENTS
      return FloorFormTabType.CLARIFICATION_REQUEST
    }
    const defaultActiveTab = defaultTab ?? getDefaultActiveTab()

    const [effectiveTab, handleTabChange] =
      useControlledState<FloorFormTabType>({
        value: activeTab,
        defaultValue: defaultActiveTab,
        onChange: onTabChange
      })

    if (!showRemarkTab && !showAttachmentsTab && !showClarificationRequestTab) {
      return null
    }

    // The selected tab can be hidden (stale sidebar state, or the default was
    // computed before attachments loaded), so fall back to a visible one.
    const visibleTabs = {
      [FloorFormTabType.REMARK]: showRemarkTab,
      [FloorFormTabType.ATTACHMENTS]: showAttachmentsTab,
      [FloorFormTabType.CLARIFICATION_REQUEST]: showClarificationRequestTab
    }
    const activeKey = visibleTabs[effectiveTab]
      ? effectiveTab
      : getDefaultActiveTab()

    return (
      <div className="floor-form__tabs">
        <Tabs
          activeKey={activeKey}
          onSelect={key =>
            handleTabChange(
              (key as FloorFormTabType) ??
                FloorFormTabType.CLARIFICATION_REQUEST
            )
          }
          className="nav-tabs"
        >
          {showRemarkTab && (
            <Tab
              eventKey={FloorFormTabType.REMARK}
              title={t("FloorForm.Tab.REMARK")}
            >
              <FloorFormRemarkTab />
            </Tab>
          )}
          {showAttachmentsTab && (
            <Tab
              eventKey={FloorFormTabType.ATTACHMENTS}
              title={t("FloorForm.Tab.ATTACHMENTS")}
            >
              <FloorFormAttachmentsTab />
            </Tab>
          )}
          {showClarificationRequestTab && (
            <Tab
              eventKey={FloorFormTabType.CLARIFICATION_REQUEST}
              title={
                <span
                  className={classNames({
                    "nav-link__content--warning": hasClarificationRequest
                  })}
                >
                  {hasClarificationRequest && (
                    <Icon
                      name={IconName.CLARIFICATION_REQUEST}
                      size={IconSize.SMALL}
                    />
                  )}
                  {t("ClarificationRequest.Form.Tab.Label")}
                </span>
              }
            >
              <div className="floor-form__tab-content">
                <FloorFormClarificationRequest />
              </div>
            </Tab>
          )}
        </Tabs>
      </div>
    )
  }
)

FloorFormTabs.displayName = "FloorFormTabs"
